import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';

const UserDetailPanel = ({ user, onUpdateUser, haspermission, onDeleteUser }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [activeSection, setActiveSection] = useState("login");
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  if (!user) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <Icon name="User" size={32} color="var(--color-primary)" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">Select a User</h3>
          <p className="text-text-secondary">Choose a user from the table to view their details and manage permissions.</p>
        </div>
      </div>
    );
  }

  const handleEdit = () => {
    setEditForm({
      company_name: user.company_name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      address: user.address,
      status: user.status,
      gst_number: user.gst_number,
      permissions: [...user.permissions]
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (saveLoading) return;
    const token = localStorage.getItem("token");
    const updatedUser = { ...user, ...editForm };
    try {
      setSaveLoading(true);
      const response = await fetch(import.meta.env.VITE_APP_ADMINURL + `/api/user-data/${updatedUser.id}`, {
        method: 'PUT',  // Use PUT method to update
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm),  // Send updated user data
      });

      if (response.ok) {
        const savedUser = await response.json();
        onUpdateUser(updatedUser);  // Call the callback to update the parent component
        setIsEditing(false);  // Exit editing mode
      } else {
        const error = await response.json();
        console.error('Error updating user:', error);
      }
    } catch (error) {
      console.error('Error communicating with the server:', error);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    if (saveLoading) return;
    setEditForm({});
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (deleteLoading) return;
    const token = localStorage.getItem('token')
    try {
      setDeleteLoading(true);
      const response = await fetch(import.meta.env.VITE_APP_ADMINURL + `/api/user-data/${user.id}`, {
        method: 'Delete',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (response.ok) {
        // condition to remove data from table
        onDeleteUser(user.id)
      } else {
        const error = await response.json();
        console.error('Failed to delete user:', error);
      }
    } finally {
      setDeleteLoading(false);
    }
  }

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatDateTime = (date) => {
    // Ensure the date is a valid Date object
    const validDate = new Date(date);

    if (isNaN(validDate)) {
      // If the date is invalid, return a fallback message or empty string
      return "Invalid Date";
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(validDate);
  };


  const isOnline = (lastActivity) => {
    if (!lastActivity) {
      return false; // Return false if lastActivity is null or undefined
    }

    return Date.now() - new Date(lastActivity).getTime() < 900000; // 15 minutes
  };

  const availablePermissions = [
    'Full Access',
    'User Management',
    'Create QR Code',
    'Create Product',
    'View Reports',
    'Export Data',
    'Audit Logs'
  ];

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'permissions', label: 'Permissions', icon: 'Shield' },
    { id: 'activity', label: 'Activity', icon: 'Activity' }
  ];

  return (
    <div className="bg-surface sticky top-[65px] rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading font-semibold text-text-primary">User Details</h3>
          {!isEditing ? (
            haspermission('User Management') && (
              <div className='flex gap-1'>
                <Button onClick={handleDelete} variant="danger" size="sm" iconName="Trash2" loading={deleteLoading}>
                  <span>Delete</span>
                </Button>
                <button
                  onClick={handleEdit}
                  className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 nav-transition"
                >
                  <Icon name="Edit" size={14} color="white" />
                  <span>Edit</span>
                </button>
              </div>
            )
          ) : (
            <div className="flex items-center space-x-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                size="sm"
                disabled={saveLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                size="sm"
                className="text-white"
                loading={saveLoading}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* User Header */}
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-4">
          <div className="relative flex-[1_0_30%]">
            <Image
              src={`${import.meta.env.VITE_APP_ADMINURL}/${user.logo}`}
              alt={user.company_name}
              className="rounded-full object-cover"
            />
            {isOnline(user.last_activity) && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success border-2 border-surface rounded-full"></div>
            )}
          </div>
          <div className="flex-[1_1_70%] overflow-hidden">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editForm.company_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent"
                />
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-border-focus focus:border-transparent"
                />
              </div>
            ) : (
              <>
                <h4 className="text-xl font-heading font-semibold text-text-primary">{user.company_name}</h4>
                <p className="text-text-secondary truncate">{user.email}</p>
              </>
            )}
            <div className="flex items-center space-x-2 mt-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.role === 'Client' ? 'bg-accent-100 text-accent-700' :
                user.role === 'Staff' ? 'bg-primary-100 text-primary-700' : 'bg-warning-100 text-warning-700'
                }`}>
                {user.role}
              </span>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-error-100 text-error-700'
                }`}>
                {user.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-1 justify-center items-center space-x-2 py-4 border-b-2 font-medium text-sm nav-transition ${activeTab === tab.id
                ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
            >
              <Icon name={tab.icon} size={16} color="currentColor" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="overflow-auto">
        {activeTab === 'profile' && (
          <div className="p-2">
            <div>
              <h5 className="text-sm font-medium text-text-primary py-3 mb-3">Contact Information</h5>
              <div className="space-y-3 px-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Phone</span>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="px-3 py-1 border border-border rounded text-sm focus:ring-2 focus:ring-border-focus focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-text-primary">{user.phone}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">GST</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.gst_number}
                      onChange={(e) => setEditForm(prev => ({ ...prev, gst_number: e.target.value }))}
                      className="px-3 py-1 border border-border rounded text-sm focus:ring-2 focus:ring-border-focus focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-text-primary">{user.gst_number}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Address</span>
                  {isEditing ? (
                    <textarea
                      type="text"
                      value={editForm.address}
                      onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                      className="px-3 py-1 border border-border rounded text-sm focus:ring-2 focus:ring-border-focus focus:border-transparent"
                    />
                  ) : (
                    <span className="text-sm text-text-primary">{user.address}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Join Date</span>
                  <span className="text-sm text-text-primary">{formatDate(user.joinDate)}</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="text-sm font-medium text-text-primary py-3">Account Settings</h5>
              <div className="flex flex-col px-4 gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Role</span>
                  {isEditing ? (
                    <select
                      value={editForm.role}
                      onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                      className="px-3 py-1 border border-border rounded text-sm focus:ring-2 focus:ring-border-focus focus:border-transparent"
                    >
                      <option value="Admin">Admin</option>
                      <option value="Staff">Staff</option>
                      <option value="Client">Client</option>
                    </select>
                  ) : (
                    <span className="text-sm text-text-primary">{user.role}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Status</span>
                  {isEditing ? (
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                      className="px-3 py-1 border border-border rounded text-sm focus:ring-2 focus:ring-border-focus focus:border-transparent"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  ) : (
                    <span className="text-sm text-text-primary">{user.status}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Last Activity</span>
                  <span className="text-sm text-text-primary">{user.last_activity ?? formatDateTime(user.last_activity)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'permissions' && (
          <div className="space-y-4 h-56">
            <h5 className="text-sm font-medium text-text-primary">Permission Matrix</h5>
            <div className="space-y-2">
              {availablePermissions.map((permission) => (
                <div key={permission} className="flex items-center justify-between py-2">
                  <span className="text-sm text-text-primary">{permission}</span>
                  {isEditing ? (
                    <input
                      type="checkbox"
                      checked={editForm.permissions?.includes(permission)}
                      onChange={(e) => {
                        const newPermissions = e.target.checked
                          ? [...(editForm.permissions || []), permission]
                          : (editForm.permissions || []).filter(p => p !== permission);
                        setEditForm(prev => ({ ...prev, permissions: newPermissions }));
                      }}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                    />
                  ) : (
                    <div className={`w-4 h-4 rounded flex items-center justify-center ${user.permissions.includes(permission) ? 'bg-success text-white' : 'bg-gray-200'
                      }`}>
                      {user.permissions.includes(permission) && (
                        <Icon name="Check" size={12} color="white" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <div className='flex gap-2'>
              <div className={`border-b-2 flex-1 text-center ${activeSection === "login" ? "border-primary" : "border-transparent"}`}>
                <h5 className={`text-sm cursor-pointer font-medium text-text-primary py-3`} onClick={() => { setActiveSection("login") }}>Recent Login History</h5>
              </div>
              <div className={`border-b-2 flex-1 text-center ${activeSection === "activity" ? "border-primary" : "border-transparent"}`}>
                <h5 className={`text-sm cursor-pointer font-medium text-text-primary py-3`} onClick={() => { setActiveSection("activity") }}>Activity Log</h5>
              </div>
            </div>
            {activeSection === 'login' &&
              <div className="h-56 overflow-auto px-4">
                {user.login_history?.length > 0 ?
                  (user.login_history.map((login, index) => (
                    <div key={index} className="py-2 border-b border-border last:border-b-0">
                      <p className="text-sm text-text-primary">{login.device}</p>
                      <div className='flex justify-between'>
                        <span className="text-xs text-text-secondary">{formatDateTime(login.date)}</span>
                        <p className="text-xs text-text-secondary">IP Address: {login.ip}</p>
                      </div>
                    </div>
                  ))) : (
                    <p className="text-sm text-text-secondary justify-self-center py-20">No login history available</p>
                  )}
              </div>}
            {activeSection === 'activity' &&
              <div className="h-56 overflow-auto px-4">
                {user.audit_log?.length > 0 ? (user.audit_log.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 py-2">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                    <div className="flex-1">
                      <p className="text-sm text-text-primary">{activity.description}</p>
                      <p className="text-xs text-text-secondary">{formatDateTime(activity.createdAt)}</p>
                    </div>
                  </div>
                ))) : (
                  <p className="text-sm text-text-secondary justify-self-center py-20">No activity log available</p>
                )}
              </div>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailPanel;
